import SectionContainer from '@common/components/SectionContainer/index';
import { Routes, EMPTY_CONTENT_PAGE } from '@common/constants';
import { useLabels } from '@features/Menu/useLabels';
import { useRoomPageSettings } from '@features/rooms';
import { useModuleStatuses } from '@features/properties';
import { useTranslations } from '@lang';
import { Hero } from '@features/Home/Hero';
import { FeaturedCategories } from '@features/Home/components/FeaturedCategories';
import { FeaturedEntity } from '@features/Home/components/FeaturedEntity';
import { FeaturedLink } from '@features/Home/components/FeaturedLink';
import FeaturedProducts from '@features/Home/components/FeaturedProducts';
import { FC } from 'react';
import { HomeProps } from './Home.types';
import { useFeaturedModuleVisibility } from './useFeaturedModuleVisibility';
import useRooms from './useRooms';
import {
  useGetAllSpaPackages,
  useGetAvailableSpaPackages,
  useGetSpaDaysPage,
  useGetSpaTreatments,
  useGetSpaTreatmentsPage,
} from '@features/spa/hooks';
import moment from 'moment';
import { mapSpaEntitiesWithPrice } from './mapSpaEntitiesWithPrice';

export const Home: FC<HomeProps> = ({
  homepage = {} as HomeProps['homepage'],
  journeyParams,
  language,
  property,
  tablesPage = {
    title: '',
    content: '',
    heroBackgroundImage: { url: '' },
  },
}) => {
  const { isFeaturedSpaDaysVisible, isFeaturedSpaTreatmentsVisible } =
    useFeaturedModuleVisibility();

  const { t } = useTranslations();

  const {
    statuses: { isRoomsEnabled },
  } = useModuleStatuses();
  const labels = useLabels();

  const { content: roomsPage } = useRoomPageSettings(language, t('home.rooms'), isRoomsEnabled);
  const { data: spaTreatmentsPageContent } = useGetSpaTreatmentsPage(property.id, language);
  const spaTreatmentsPage = spaTreatmentsPageContent ?? EMPTY_CONTENT_PAGE;
  const arrivalDate = moment(journeyParams?.spaDate || journeyParams?.fromDate).format(
    'YYYY-MM-DD'
  );

  const { spaTreatments: spaTreatmentsStatic } = useGetSpaTreatments(
    property.id,
    language,
    null,
    null,
    6,
    true
  );
  const { spaTreatments: spaTreatmentsWithPrice, isFetching: isLoadingAvailableSpaTreatments } =
    useGetSpaTreatments(property.id, language, arrivalDate, null, 6, true);

  const { data: spaPackagesDetails = [] } = useGetAllSpaPackages(property.id, language);

  const { data: spaPackagesWithPrice = [], isFetching: isLoadingAvailableSpaPackages } =
    useGetAvailableSpaPackages(
      property.id,
      { date: arrivalDate, perPage: 6, category: null },
      language
    );

  const spaTreatments = mapSpaEntitiesWithPrice(spaTreatmentsStatic, spaTreatmentsWithPrice);

  const spaPackages = mapSpaEntitiesWithPrice(spaPackagesDetails, spaPackagesWithPrice);

  const { data: spaDaysPageData } = useGetSpaDaysPage(property.id, language);
  const spaDaysPage = spaDaysPageData ?? EMPTY_CONTENT_PAGE;
  const { rooms, isFetching: isLoadingRooms } = useRooms(journeyParams, language);

  const renderSpaTreatmentsTitle = () => {
    if (spaTreatmentsPage.title) return spaTreatmentsPage.title;

    if (labels.treatments) return labels.treatments;

    return t('home.spaTreatments');
  };

  const renderSpaDaysPageTitle = () => {
    if (spaDaysPage.title) return spaDaysPage.title;

    if (labels.spadays) return labels.spadays;

    return t('home.spaDays');
  };

  return (
    <>
      {homepage.heroTitle && (
        <Hero
          title={homepage.heroTitle}
          backgroundUrl={homepage.heroBackgroundImage ? homepage.heroBackgroundImage.url : ''}
          journeyParams={journeyParams}
          property={property}
          showAsMinimalLayout={false}
        />
      )}

      {isRoomsEnabled && rooms.length > 0 ? (
        <FeaturedEntity
          title={roomsPage.title ?? (labels?.rooms || t('home.rooms'))}
          description={roomsPage.description ?? ''}
          items={rooms}
          route={Routes.ROOM_DETAIL}
          entityListingRoute={Routes.ROOM_LIST}
          entityName={labels?.rooms || t('home.rooms')}
          dataTestId="featured-entity-room"
          isLoadingPrice={isLoadingRooms}
        />
      ) : null}

      {property.isTablesIntegrated && tablesPage && tablesPage.heroBackgroundImage ? (
        <SectionContainer>
          <FeaturedLink
            item={{
              ...tablesPage,
              subtitle: tablesPage.content,
              image: tablesPage.heroBackgroundImage,
            }}
          />
        </SectionContainer>
      ) : null}

      {isFeaturedSpaDaysVisible && (
        <FeaturedEntity
          title={renderSpaDaysPageTitle()}
          description={spaDaysPage.content}
          items={spaPackages}
          route={Routes.SPA_DAY_DETAIL}
          entityListingRoute={Routes.SPA_DAY_LIST}
          entityName={labels?.spadays || t('home.spaDays')}
          isLoadingPrice={isLoadingAvailableSpaPackages}
        />
      )}

      {isFeaturedSpaTreatmentsVisible && (
        <FeaturedEntity
          title={renderSpaTreatmentsTitle()}
          description={spaTreatmentsPage.content}
          items={spaTreatments}
          route={Routes.SPA_TREATMENT_DETAIL}
          entityListingRoute={Routes.SPA_TREATMENT_LIST}
          entityName={labels?.treatments || t('home.spaTreatments')}
          isLoadingPrice={isLoadingAvailableSpaTreatments}
        />
      )}

      {homepage?.featuredItem ? (
        <SectionContainer>
          <FeaturedLink item={homepage.featuredItem} />
        </SectionContainer>
      ) : null}

      {property.isRetailEnabled && homepage?.otherItems?.length ? (
        <FeaturedProducts
          propertyName={property.name}
          products={homepage.otherItems.map(({ product }) => product).filter((product) => product)}
        />
      ) : null}

      {homepage?.categories?.length ? (
        <FeaturedCategories categories={homepage.categories} />
      ) : null}
    </>
  );
};
